import React, { FC, useEffect } from 'react';
import { isEditorActive } from '@sitecore-jss/sitecore-jss-react';
import { createPortal } from 'react-dom';
import useMediaQuery from '../../hooks/useMediaQuery';
import breakpoints from '../../constants/breakpoints.constants';
import { FLOATING_MENU_LINK_TYPES } from '../../constants/floatingMenu.constants';
import useEEActive from '../../hooks/useEEActive';
import useExperienceEditorPlaceholder from '../../hooks/useExperienceEditorPlaceholder';
import useStickyWrapper from '../../hooks/useStickyWrapper';
import { isEmptyResponse } from '../../utils/checkEmptyResponse';
import { windowService, documentService } from '../../utils/window.utils';
import EmptyApiDataMessage from '../@shared/emptyApiDataMessage';
import ExperienceEditorComponentPlaceholder from '../@shared/experienceEditorComponentPlaceholder';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import FloatingMenuLink from './components/floatingMenuLink';
import { ProductFloatingMenuModel } from './models/productFloatingMenu.model';
import {
  TProductFloatingMenuBuildAndPriceItem,
  TProductFloatingMenuContactUsItem,
  TProductFloatingMenuGeneralLink,
  TProductFloatingMenuProps,
} from './types/productFloatingMenu.types';
import ContactUsLink from './components/contactUsLink';
import BuildAndPriceLink from './components/buildAndPriceLink';
import './styles.scss';

const ProductFloatingMenu: FC<TProductFloatingMenuProps> = (props) => {
  const model = new ProductFloatingMenuModel(props);
  const isWide = useMediaQuery(`(min-width: ${breakpoints['tbl-p']}px)`, true);
  const floatingMenuItemsToLoad = model.getFloatingMenuItemsToLoad(isWide);
  const apiResponse = isEmptyResponse(props) && floatingMenuItemsToLoad.length > 0;
  const isPlaceholderShown = useExperienceEditorPlaceholder(props);
  const stickyWrapperEle = useStickyWrapper();
  const isEEactive = useEEActive();

  useEffect(() => {
    if (!windowService()?.innerWidth) return;
    if (windowService()?.innerWidth < breakpoints['tbl-p'] && floatingMenuItemsToLoad?.length > 0) {
      documentService().body.style.setProperty('--floating-menu-mbl-height', '64px');
    }
    return (): void => {
      documentService().body.style.setProperty('--floating-menu-mbl-height', 0);
    };
  }, [floatingMenuItemsToLoad, props]);

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  // star  node opened directly in EE
  if (isPlaceholderShown) {
    return <ExperienceEditorComponentPlaceholder image={model.thumbnailEE} componentName={model.componentName} />;
  }

  // not opened from star node
  // data missing when navigating via from products node
  if (!apiResponse && isEditorActive()) {
    return <EmptyApiDataMessage componentName={model.componentName} />;
  }

  // No data and not in EE
  if (!apiResponse && !isPlaceholderShown && !isEditorActive()) {
    return null;
  }

  const floatingLinksList = floatingMenuItemsToLoad.map((eachFloatingMenuItem) => {
    const { id, fields, linkType } = eachFloatingMenuItem;
    if ((linkType as FLOATING_MENU_LINK_TYPES) === FLOATING_MENU_LINK_TYPES.CONTACT_US)
      return <ContactUsLink contactUsItem={eachFloatingMenuItem as TProductFloatingMenuContactUsItem} rendering={props?.rendering} />;
    if ((linkType as FLOATING_MENU_LINK_TYPES) === FLOATING_MENU_LINK_TYPES.BUILD_AND_PRICE)
      return <BuildAndPriceLink buildAndPriceItem={eachFloatingMenuItem as TProductFloatingMenuBuildAndPriceItem} />;

    return (
      <FloatingMenuLink
        key={id}
        itemId={id}
        title={fields.title}
        icon={fields.icon}
        link={(fields as TProductFloatingMenuGeneralLink['fields']).link}
      />
    );
  });

  const floatingLinkMenu = (
    <div className="floating-links">
      <ul className="floating-links__wrapper">{floatingLinksList}</ul>
    </div>
  );

  if (isEEactive) return floatingLinkMenu;

  if (!stickyWrapperEle) return null;

  return <section>{createPortal(floatingLinkMenu, stickyWrapperEle)}</section>;
};

export default ProductFloatingMenu;
